import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskLine } from "../components/Reveal";
import { ShareCatch } from "../components/ShareCatch";

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const gallery = items.filter((t) => t.image);
  const sidebar = items.filter((t) => !t.image && t.quote);

  return (
    <div className="bg-[#04070A] min-h-screen" data-testid="testimonials-page">
      <Nav />
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-36 pb-14">
        <MaskLine delay={0.1}>
          <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-orange-500">From the Water</span>
        </MaskLine>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-5">
          <h1 className="font-display text-6xl sm:text-8xl uppercase tracking-tighter leading-[0.85]">
            <MaskLine delay={0.25}>Angler Proof</MaskLine>
          </h1>
          <ShareCatch label="Share Your Catch" testid="testimonials-share" />
        </div>
        <p className="text-slate-400 text-lg leading-relaxed mt-6 max-w-xl">
          Real catches, real words from anglers running Drift baits. Landed a good one? Add it to the wall.
        </p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        {loading ? (
          <p className="text-slate-500 font-mono-plex text-sm tracking-widest uppercase">Loading…</p>
        ) : gallery.length === 0 && sidebar.length === 0 ? (
          <p className="text-slate-500 font-mono-plex text-sm tracking-widest uppercase" data-testid="testimonials-empty">
            No testimonials yet — be the first to share your catch.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {/* LEFT 2/3 — photo gallery */}
            <div className="lg:col-span-2" data-testid="testimonials-gallery">
              {gallery.length === 0 ? (
                <p className="text-slate-500 font-mono-plex text-sm tracking-widest uppercase">
                  No catch photos yet — share yours above.
                </p>
              ) : (
                <div className="columns-1 sm:columns-2 gap-6 [column-fill:_balance]">
                  {gallery.map((t, i) => (
                    <motion.div
                      key={t.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.19, 1, 0.22, 1] }}
                      className="mb-6 break-inside-avoid border border-white/5 bg-[#0A1118] overflow-hidden"
                      data-testid={`testimonial-${t.id}`}
                    >
                      <div className="overflow-hidden">
                        <img src={fileUrl(t.image)} alt={t.name || "Customer catch"} className="w-full object-cover" loading="lazy" />
                      </div>
                      {(t.quote || t.name || t.location) && (
                        <div className="p-5">
                          {t.quote && <p className="text-slate-200 text-base leading-relaxed">{t.quote}</p>}
                          {(t.name || t.location) && (
                            <div className={t.quote ? "mt-4" : ""}>
                              {t.name && <p className="font-display uppercase tracking-tight text-sm">{t.name}</p>}
                              {t.location && <p className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 mt-1">{t.location}</p>}
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT 1/3 — text testimonials */}
            <aside className="lg:col-span-1" data-testid="testimonials-sidebar">
              <div className="lg:sticky lg:top-28">
                <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">In Their Words</span>
                <div className="mt-6 flex flex-col gap-5">
                  {sidebar.length === 0 ? (
                    <p className="text-slate-600 text-sm">More words from the water coming soon.</p>
                  ) : (
                    sidebar.map((t, i) => (
                      <motion.div
                        key={t.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.06 }}
                        className="border-l-2 border-orange-500/40 pl-5 py-1"
                        data-testid={`testimonial-${t.id}`}
                      >
                        <Quote className="w-4 h-4 text-orange-500 mb-2" />
                        <p className="text-slate-300 text-sm leading-relaxed">{t.quote}</p>
                        {(t.name || t.location) && (
                          <p className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 mt-3">
                            {t.name}{t.name && t.location ? " · " : ""}{t.location}
                          </p>
                        )}
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
